import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Avatar, Pagination, Grid
} from '@mui/material';
import { Refresh, Search, FamilyRestroom } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return 'https://api.bharatdarshan.app' + url;
};

const SanataniKutumbMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 25 };
      if (search) params.search = search;
      const res = await axios.get(`${API_BASE}/admin/kutumb-members`, { params, headers: getAuthHeaders() });
      if (res.data.success) {
        setMembers(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch (err) {
      setError('Error loading kutumb members: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#795548' }}>
          <FamilyRestroom sx={{ verticalAlign: 'middle', mr: 1, fontSize: 36 }} />
          Kutumb Members
        </Typography>
        <IconButton onClick={fetchMembers} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" component="form" onSubmit={handleSearch}>
          <Grid item xs={12} sm={8} md={9}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name / relation / mobile..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} /> }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button type="submit" variant="contained" fullWidth>Search</Button>
          </Grid>
        </Grid>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Total Kutumb Members: <b>{total}</b>
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : members.length === 0 ? (
        <Alert severity="info">No Kutumb members found.</Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#EFEBE9' }}>
                  <TableCell>Photo</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Relation</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>Owner (Profile)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m._id} hover>
                    <TableCell>
                      <Avatar src={getFullImageUrl(m.photo || m.linkedProfile?.profilePhoto)} sx={{ width: 36, height: 36 }}>
                        {(m.name || '?').charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>{m.title ? `${m.title} ` : ''}{m.name}</TableCell>
                    <TableCell><Chip label={m.relation} size="small" /></TableCell>
                    <TableCell>{m.mobile || m.linkedProfile?.mobile || '-'}</TableCell>
                    <TableCell>{m.dateOfBirth ? new Date(m.dateOfBirth).toLocaleDateString('en-IN') : '-'}</TableCell>
                    <TableCell>{m.owner?.name || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" />
          </Box>
        </>
      )}
    </Box>
  );
};

export default SanataniKutumbMembers;
